import { useState } from 'react';
import { CirclePlus, IdCard, Copy, Check } from 'lucide-react';

import CartSummary from './CartSummary';
import CartItem from './components/viewCartForCardPage/CartItem';

import AddCartItemDialog from '@/app/cartItem/AddCartItemDialog';

import Spinner from '@/components/common/Spinner';
import { Separator } from '@/components/ui/separator';
import Error from '@/components/common/Error';
import { Button } from '@/components/ui/button';

import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { useCart, useDeleteItemFromCart } from '@/data/cart';
import { useCard } from '@/data/card';

const ViewCartForCardPage = ({ cartId }) => {
  const card = useCard();
  const [apiError, setApiError] = useState('');
  const cart = useCart(cartId);
  const [isAddItemOpen, setIsAddItemOpen] = useState(false);
  const deleteItemFromCart = useDeleteItemFromCart();
  const [copied, setCopied] = useState(false);

  const handleRemoveItem = async (itemId) => {
    try {
      const updatedCart = await deleteItemFromCart(cartId, itemId);
      await cart.mutate(updatedCart, false);
    } catch (err) {
      setApiError(err.message);
    }
  };

  const handleAddItem = () => {
    setIsAddItemOpen(true);
  };

  const handleAddItemSuccess = async (newCartItem) => {
    await cart.mutate(newCartItem, false);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(card.data.cardLink);
    setCopied(true);
  };

  if (cart.isLoading) {
    return (
      <div className="flex justify-center items-center h-[200px]">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Custom Cart</h2>
        <Button size="sm" onClick={handleAddItem}>
          <CirclePlus className="mr-2" size={18} /> Item
        </Button>
      </div>
      {apiError && <Error error={apiError} />}
      <div>
        <div className="mb-6 space-y-2">
          {cart.data.items.map((item) => (
            <CartItem key={item.id} item={item} onRemove={handleRemoveItem} />
          ))}
        </div>
        <Separator />
        <div className="mb-4">
          <CartSummary cart={cart.data} />
        </div>

        <Separator />
        {cart.data.items.length > 0 && (
          <div className="mt-6">
            <Alert variant="success">
              <IdCard size={18} />
              <AlertTitle>Send Cart</AlertTitle>
              <AlertDescription>
                Tap your ProFound Card to send the cart to someone else. Or...
              </AlertDescription>
              <div className="mt-4 flex justify-end">
                <Button variant="outline" size="sm" onClick={handleCopyLink}>
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Link
                    </>
                  )}
                </Button>
              </div>
            </Alert>
          </div>
        )}
      </div>
      {isAddItemOpen && (
        <AddCartItemDialog
          cartId={cartId}
          onClose={() => setIsAddItemOpen(false)}
          onSuccess={handleAddItemSuccess}
        />
      )}
    </>
  );
};

export default ViewCartForCardPage;
